/**
 * Product Image Loader
 *
 * Marks native product primary images with data-rimg="loaded" once the browser
 * finishes fetching them, so the image fades in, and stamps the enclosing
 * .productitem__image-container with .is-image-settled so the CSS skeleton
 * shimmer stops. The class exists so the stylesheet does not need a
 * :has([data-rimg="loaded"]) selector, which re-invalidates style for the whole
 * grid on every data-rimg attribute change.
 */
(function () {
  'use strict';

  var SELECTOR = '.productitem--image img.productitem--image-primary[data-rimg=""]';
  var PRIMARY_SELECTOR = 'img.productitem--image-primary';
  var CONTAINER_SELECTOR = '.productitem__image-container';
  var SETTLED_CLASS = 'is-image-settled';
  var SETTLED_STATES = { loaded: true, error: true };

  function isSettled(img) {
    return Boolean(img && SETTLED_STATES[img.getAttribute('data-rimg')]);
  }

  function markContainerSettled(img) {
    if (!img || !img.closest) return;
    var container = img.closest(CONTAINER_SELECTOR);
    if (container && !container.classList.contains(SETTLED_CLASS)) {
      container.classList.add(SETTLED_CLASS);
    }
  }

  function setRimgState(img, state) {
    img.setAttribute('data-rimg', state);
    markContainerSettled(img);
  }

  function getProductImages(root) {
    var scope = root || document;
    var imgs = [];

    if (scope.matches && scope.matches(SELECTOR)) {
      imgs.push(scope);
    }

    if (scope.querySelectorAll) {
      imgs = imgs.concat(Array.prototype.slice.call(scope.querySelectorAll(SELECTOR)));
    }

    return imgs;
  }

  /**
   * Catch primaries that another script (rimg, recommendations carousels,
   * wishlist rendering) already marked loaded before we saw the node.
   */
  function syncSettledContainers(root) {
    var scope = root || document;

    if (scope.matches && scope.matches(PRIMARY_SELECTOR) && isSettled(scope)) {
      markContainerSettled(scope);
    }

    if (!scope.querySelectorAll) return;

    var primaries = scope.querySelectorAll(PRIMARY_SELECTOR);
    for (var i = 0; i < primaries.length; i++) {
      if (isSettled(primaries[i])) {
        markContainerSettled(primaries[i]);
      }
    }
  }

  function markEagerProductImages(root) {
    var scope = root || document;
    var imgs = getProductImages(scope);

    for (var i = 0; i < imgs.length; i++) {
      (function (img) {
        if (img.complete && img.naturalWidth > 0) {
          setRimgState(img, 'loaded');
        } else if (!img.__mmProductImageLoaderBound) {
          img.__mmProductImageLoaderBound = true;
          img.addEventListener('load', function () {
            setRimgState(img, 'loaded');
          });
          img.addEventListener('error', function () {
            // Gracefully clear the skeleton even when the image 404s
            setRimgState(img, 'error');
          });
        }
      })(imgs[i]);
    }

    syncSettledContainers(scope);
  }

  // Initial run
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      markEagerProductImages();
    });
  } else {
    markEagerProductImages();
  }

  // Re-run after Shopify section injection (theme editor, live preview)
  document.addEventListener('shopify:section:load', function (event) {
    markEagerProductImages(event.target);
  });

  // Re-run when rimg begins watching a new container (AJAX collections, etc.)
  document.addEventListener('theme:rimg:watch', function (event) {
    setTimeout(function () {
      markEagerProductImages(event.target);
    }, 0);
  });

  // Re-run when product cards are injected after page load (recently viewed,
  // recommendations, wishlist refreshes, etc.).
  document.addEventListener('mm:cards:added', function (event) {
    setTimeout(function () {
      markEagerProductImages((event && event.target) || document);
    }, 0);
  });

  if ('MutationObserver' in window) {
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'attributes') {
          if (isSettled(mutation.target)) {
            markContainerSettled(mutation.target);
          }
          return;
        }

        for (var i = 0; i < mutation.addedNodes.length; i++) {
          var node = mutation.addedNodes[i];
          if (node.nodeType === 1) {
            markEagerProductImages(node);
          }
        }
      });
    });

    function observeInjectedProductCards() {
      if (!document.body) return;
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-rimg'],
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', observeInjectedProductCards, { once: true });
    } else {
      observeInjectedProductCards();
    }
  }
})();
